import { useEffect, type ReactNode } from 'react';
import { ReactLenis, useLenis } from 'lenis/react';
import { gsap, ScrollTrigger, prefersReducedMotion } from './gsap';

/**
 * App-wide smooth scrolling. Lenis is driven by GSAP's ticker (instead of its own rAF
 * loop) so smooth scroll and ScrollTrigger animations update in the same frame — this is
 * what removes the "reveal lags behind scroll" jitter. Native scrolling is used when the
 * user prefers reduced motion, and on touch devices (native momentum feels better there).
 */
export function SmoothScroll({ children }: { children: ReactNode }) {
  if (prefersReducedMotion()) return <>{children}</>;

  return (
    <ReactLenis
      root
      options={{
        autoRaf: false,
        lerp: 0.1,
        wheelMultiplier: 1,
        smoothWheel: true,
        syncTouch: false,
        // Nested scroll areas (modals, drawers, pickers) scroll natively.
        prevent: (node: HTMLElement) =>
          node.hasAttribute('data-lenis-prevent') ||
          node.closest('[role="dialog"], [data-radix-scroll-area-viewport], [data-vaul-drawer]') !== null,
      }}
    >
      <LenisTicker />
      {children}
    </ReactLenis>
  );
}

/**
 * Drives Lenis from GSAP's ticker. Must live *inside* <ReactLenis>: useLenis() re-renders
 * once the instance exists (it is created in an effect), so the ticker is attached as soon
 * as Lenis is ready. With autoRaf disabled, Lenis would otherwise swallow wheel events and
 * never move the page.
 */
function LenisTicker() {
  const lenis = useLenis();

  useEffect(() => {
    if (!lenis) return;

    const update = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(update);
    gsap.ticker.lagSmoothing(0);
    lenis.on('scroll', ScrollTrigger.update);

    return () => {
      gsap.ticker.remove(update);
      lenis.off('scroll', ScrollTrigger.update);
    };
  }, [lenis]);

  return null;
}
